import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import { ForwardedRef, MouseEvent, Suspense, lazy, useState } from 'react';

import useFormatImageUrl from '../../hooks/useFormatImageUrl';
import { useUpdateAvatar } from '../../hooks/useProfile';
import { getImageUrl } from '../../lib/getImageSize';

import LoadingIcon from '../icons/LoadingIcon';
import BasicButton, { BasicButtonElement, BasicButtonProps } from './BasicButton';

import styles from '../../styles/buttons/avatar-button.module.css';

import { ReactComponent as CameraIcon } from '../../assets/img/icons/camera.svg';

const UploadModal = lazy(() => import('../popups/UploadModal'));

interface AvatarButtonProps extends BasicButtonProps {
    withCameraIcon?: boolean;
}

function AvatarButton({
    className = null,
    withCameraIcon = false,
    onClick = null,
    ref: buttonRef = null,
    ...props
}: AvatarButtonProps & { ref?: ForwardedRef<BasicButtonElement> | null }) {
    const [opened, setOpened] = useState(false);
    const { updateAvatarAsync, isPending: loading = false } = useUpdateAvatar();
    const { image = null, name = null, firstname = null, lastname = null } = useUser() || {};
    const finalName = name ?? [firstname, lastname].filter((it) => it !== null).join(' ');
    const initials =
        finalName !== null && finalName !== ''
            ? finalName
                  .split(' ')
                  .reduce((map, n) => [...map, ...n.split('-')], [] as string[])
                  .map((n) => n[0])
                  .join('')
            : null;
    const formatImageUrl = useFormatImageUrl();
    const url = formatImageUrl(getImageUrl(image, 'thumbnail'));

    const onClickButton = (e: MouseEvent<BasicButtonElement>) => {
        onClick?.(e);
        setOpened(true);
    };

    const onCancel = () => {
        setOpened(false);
    };

    const onComplete = (newFile: { uploadURL: string }) => {
        const { uploadURL } = newFile;
        updateAvatarAsync(uploadURL).then(() => {
            setOpened(false);
        });
    };

    return (
        <>
            <BasicButton
                ref={buttonRef}
                className={classNames([
                    styles.container,
                    {
                        [styles.withCameraIcon]: withCameraIcon,
                        [styles.withAvatar]: url !== null,
                    },
                    className,
                ])}
                onClick={onClickButton}
                {...props}
            >
                <span className={styles.shape}>
                    {url !== null ? (
                        <img src={url} className={styles.image} />
                    ) : (
                        <span className={styles.initials}>{initials}</span>
                    )}
                    <span className={styles.bottom}>
                        <CameraIcon className={styles.icon} />
                    </span>
                    {loading ? <LoadingIcon className={styles.loading} /> : null}
                </span>
            </BasicButton>
            <Suspense fallback={null}>
                {opened ? <UploadModal onCancel={onCancel} onComplete={onComplete} /> : null}
            </Suspense>
        </>
    );
}

export default AvatarButton;
